/**
 * Trois VRAIES SKILLCARDS publiées, figées ici pour l'éventail de l'accueil
 * `/decouverte` (et de la fin de la démo) : rendu hors ligne, sans compte ni
 * requête, avec la face verso partagée (`CardVerso`). Copié de `cards` le
 * 29/10/2026 (`short_code` STKMGQ, D852UE, TC8UB9) — si une carte change de
 * créature ou de titre, il suffit de recopier la ligne.
 */

import type { CardRarity, CardType } from '@jeraisonne/db/cards';

export interface DecouverteCard {
  id: string;
  shortCode: string;
  title: string;
  cardType: CardType;
  rarity: CardRarity;
  domainLabel: string;
  xpMax: number;
  levels: string[];
  latex: string;
  creature: unknown;
}

export const DECOUVERTE_CARDS: readonly DecouverteCard[] = [
  {
    id: 'fd097ed0-1612-4f6a-97b1-2fdaa9ee4fc5',
    shortCode: 'D852UE',
    title: 'Réciproque de Thalès',
    cardType: 'theoreme',
    rarity: 'epique',
    domainLabel: 'Géométrie',
    xpMax: 758,
    levels: ['3e', '2nde'],
    latex: '\\frac{AM}{AB} = \\frac{AN}{AC} \\;\\Longrightarrow\\; (MN) \\parallel (BC)',
    creature: {
      species: 'grue',
      base: [
        { fill: 0.22, points: '70.3,57.7 51.1,48.2 51.5,68.4' },
        { fill: -0.12, points: '51.6,49.4 32.5,57.4 51.5,68.9' },
        { fill: 0.37, points: '71.2,57.6 51.4,47.9 78.3,15.7' },
        { fill: 0.11, points: '51.7,48.6 77.9,15.9 59.8,40.4' },
        { fill: -0.23, points: '53.2,49.2 33.4,56.6 25,17.2' },
        { fill: -0.01, points: '52.7,47.3 26.8,15.2 45.6,40.6' },
        { fill: 0.28, points: '41,53.7 32.5,58.1 20,32.4 24.2,31.9' },
        { fill: 0.14, points: '22.8,31.4 19.6,33.1 11.8,28.1 18.3,25.9' },
        { fill: 'gold', points: '11.6,29 6.3,29.3 11.6,30.8' },
        { fill: 'ink', points: '19.7,27.9 17.3,27.9 18.4,30.3' },
        { fill: -0.16, points: '70.7,57.4 64.1,61 91.4,39.8 89.2,37' },
        { fill: -0.32, points: '63.7,63.4 52.2,70 39.8,63.2 51.5,77.4' },
        { fill: 0.26, points: '62.9,67.2 53.4,69.6 58.8,81.3' },
        { fill: 0.06, points: '41.8,66.4 53.5,68.8 47.6,80.4' },
        { fill: 'blush', points: '20.8,30.4 18.6,30.1 20.7,33' },
      ],
      evolvedBack: [
        { fill: 0.57, points: '79,16.1 87.9,4.9 71.1,19.1' },
        { fill: -0.13, points: '26.3,16.9 17.3,5.6 32.6,20.8' },
        { fill: 0.35, points: '89.9,39.9 99.7,30.4 86.6,37' },
        { fill: -0.25, points: '87.1,38.1 98.7,43.7 86.6,39.7' },
      ],
      evolvedFront: [
        { fill: 'gold-light', points: '21.1,26.4 19.2,16 15.2,25.4' },
        { fill: 'gold', points: '78.6,16.4 75.1,25.8 70.5,20.6' },
        { fill: 'gold-deep', points: '26.8,16.7 30.6,25.9 33.4,20.9' },
        { fill: 'gold', points: '58.7,58.8 51.8,56.8 46.6,58.6 51.9,64' },
      ],
    },
  },
  {
    id: '1a08d732-7d31-4977-bacc-226841d2591d',
    shortCode: 'STKMGQ',
    title: 'Calculer une longueur avec Pythagore',
    cardType: 'methode',
    rarity: 'normale',
    domainLabel: 'Géométrie',
    xpMax: 500,
    levels: ['4e', '3e', '2nde'],
    latex: 'BC^2 = AB^2 + AC^2',
    creature: {
      species: 'grue',
      base: [
        { fill: 0.05, points: '70.5,60.1 50.8,50.6 51.9,70.3' },
        { fill: -0.23, points: '50.9,51.1 33.4,60.3 52.2,71.5' },
        { fill: 0.35, points: '70.5,60.9 50.7,50 77.2,17.2' },
        { fill: -0.02, points: '51.9,50.1 78.3,17.7 59.4,42.9' },
        { fill: -0.38, points: '50.3,49.4 33.6,58.8 25.8,17.7' },
        { fill: -0.04, points: '50.8,49.6 23.8,17.2 44.2,42.2' },
        { fill: 0.23, points: '40.6,57.5 32.2,59.6 18.9,35.4 21.9,34.1' },
        { fill: 0.1, points: '22.3,32.7 18.4,36.4 10.6,31.1 19.4,27' },
        { fill: 'gold', points: '11.5,30.8 4.5,31.8 11.3,33.6' },
        { fill: 'ink', points: '18.8,30.1 16.4,30.1 17.6,32.5' },
        { fill: -0.31, points: '71.1,59.7 64.3,63.2 90.8,42.2 88.4,40' },
        { fill: -0.49, points: '63.3,63.6 50.8,71.2 39.1,63.7 50.7,77.9' },
        { fill: 0.18, points: '59.8,70.4 51.5,72.1 56.3,81.4' },
        { fill: -0.06, points: '41.4,69.9 51,71.8 44.7,83.5' },
        { fill: 'blush', points: '21.2,33.2 18.4,34.1 18.2,35.2' },
      ],
      evolvedBack: [
        { fill: 0.4, points: '78.9,18.7 86.8,7 70.1,22.8' },
        { fill: -0.16, points: '24.3,19.3 15.3,6.7 31.8,22.8' },
      ],
      evolvedFront: [
        { fill: 'gold-light', points: '20.3,28.2 17.2,18.8 14.9,29.4' },
        { fill: 'gold', points: '78.2,19.2 72.1,29.7 68.3,22' },
      ],
    },
  },
  {
    id: 'cab69026-2813-4cee-b034-f78396826e73',
    shortCode: 'TC8UB9',
    title: 'Identité remarquable (a + b)²',
    cardType: 'formule',
    rarity: 'extraordinaire',
    domainLabel: 'Calcul littéral',
    xpMax: 600,
    levels: ['3e', '2nde'],
    latex: '(a + b)^2 = a^2 + 2ab + b^2',
    creature: {
      species: 'renard',
      base: [
        { fill: 0.38, points: '73.1,21.2 51.1,13.3 31.5,20 33.8,40.5 52.5,33.9 69.8,41' },
        { fill: 0.24, points: '73.6,21.4 69,41 83,44.8 87.2,30' },
        { fill: -0.01, points: '30.7,21.5 34,39.7 20.8,45.3 17.7,30.1' },
        { fill: 0.6, points: '86.8,29.5 74,21.8 78.4,12.1 91.3,19.5' },
        { fill: -0.11, points: '17.5,29.9 31.3,20.7 25.4,13.5 13.2,18.1' },
        {
          fill: 0.22,
          points: '82.9,45.6 70.6,39.7 52.3,34.8 34.6,39.2 22.1,44.5 28.1,63.8 52.9,58.2 77.3,63.6',
        },
        { fill: 0.56, points: '76.2,62.9 53,60.2 52.1,81.6 71.7,77.7' },
        { fill: 0.08, points: '26.4,63.6 52.7,59.2 52.1,82.3 33.9,78.5' },
        { fill: 0.33, points: '70.1,78.7 53,81.9 54.8,89.8 73.3,87.8' },
        { fill: 0.05, points: '32.2,78 51.5,81.8 49.2,90.1 30.7,87.4' },
        { fill: -0.18, points: '64.1,37.5 57.7,36.7 59.3,43.7 64.8,45.5' },
        { fill: -0.18, points: '46.1,35.7 39.9,38.9 38.5,46.5 44,44.8' },
        { fill: 0.69, points: '61.7,45.7 54,44.7 55,52.4 62.7,53.4' },
        { fill: 0.65, points: '50.2,44.7 42.5,45.7 41.5,53.4 49.2,52.4' },
        { fill: 'ink', points: '58.1,49.7 45.7,49 52.1,55.8' },
        { fill: 'blush', points: '57,56.3 52.2,59 48.9,55.8 51.9,59' },
        { fill: 0.62, points: '78.3,12.7 73.1,21.3 80.8,25.1 85.7,16.3' },
        { fill: -0.13, points: '25.9,14 32.1,20.6 23.5,24.6 18.1,16.8' },
        { fill: 0.4, points: '68.2,62.1 53.2,59 53.3,67.5 66,68.9' },
        { fill: 0.2, points: '36.7,60 51.9,58.1 53.3,67.2 38.5,69.8' },
      ],
      evolvedBack: [
        { fill: 0.31, points: '86.2,31 95.9,22.6 92.2,40.5 82.8,44.6' },
        { fill: -0.08, points: '18.2,30.1 9.9,23.3 11,39.8 21.3,46.9' },
        { fill: 0.42, points: '71.8,77.7 90.3,73.7 82.9,88.7 73.8,87' },
      ],
      evolvedFront: [
        { fill: 'gold-light', points: '60.1,14.7 51.6,7 44.1,14.1 48.3,20.4 55.1,21.7' },
        { fill: 'gold-deep', points: '53,6.8 45.2,14.3 47.7,10.1' },
        { fill: 'gold', points: '77.9,12.1 87.4,7.4 82.9,17.4 75.4,18' },
      ],
    },
  },
];
